import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Calendar, Lightbulb, Utensils, Hotel, Camera,
  ChevronRight, Lock, CheckCircle, Star, Clock, Map,
  ArrowLeft, Car, Backpack, PiggyBank, Heart, Sparkles, Train, Truck,
  Droplets, Shield, Smartphone, Wifi, Mountain, Bug, Anchor, Wine, Cake, Umbrella,
  FileDown, ClipboardList
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import InteractiveMap from '../components/InteractiveMap';
import GuideChecklist from '../components/GuideChecklist';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  calendar: Calendar,
  backpack: Backpack,
  car: Car,
  'piggy-bank': PiggyBank,
  heart: Heart,
  sparkles: Sparkles,
  train: Train,
  truck: Truck,
  lightbulb: Lightbulb,
  utensils: Utensils,
  droplets: Droplets,
  shield: Shield,
  smartphone: Smartphone,
  wifi: Wifi,
  hiking: Mountain,
  mountain: Mountain,
  bug: Bug,
  anchor: Anchor,
  wine: Wine,
  cake: Cake,
  umbrella: Umbrella,
  'cloud-rain': Droplets
};

export const PlaceCard = ({ place, language, locked = false }) => {
  const [expanded, setExpanded] = useState(false);
  
  const name = language === 'it' ? place.name_it : place.name_en;
  const description = language === 'it' ? place.description_it : place.description_en;
  const tips = language === 'it' ? place.tips_it : place.tips_en;
  
  // Extended content
  const extendedDescription = language === 'it' ? place.extended_description_it : place.extended_description_en;
  const practicalInfo = language === 'it' ? place.practical_info_it : place.practical_info_en;
  const bestTime = language === 'it' ? place.best_time_it : place.best_time_en;
  const duration = language === 'it' ? place.duration_it : place.duration_en;
  const whatToBring = language === 'it' ? place.what_to_bring_it : place.what_to_bring_en;
  const insiderTips = language === 'it' ? place.insider_tips_it : place.insider_tips_en;
  
  const hasExtendedContent = extendedDescription || practicalInfo || bestTime;

  const categoryIcons = {
    sightseeing: Camera,
    restaurant: Utensils,
    hotel: Hotel,
    activity: MapPin
  };
  const Icon = categoryIcons[place.category] || MapPin;

  const handleCardClick = () => {
    if (!locked && hasExtendedContent) {
      setExpanded(!expanded);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      layout
      className={`group bg-[#2C2C2E] rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:shadow-black/30 transition-all duration-300 border border-[#3A3A3C] ${locked ? 'opacity-60' : ''} ${hasExtendedContent && !locked ? 'cursor-pointer' : ''}`}
    >
      <div 
        className="relative h-48 overflow-hidden"
        onClick={handleCardClick}
      >
        <img 
          src={place.image_url} 
          alt={name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 bg-[#2C2C2E]/90 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5">
          <Icon className="w-4 h-4 text-[#C9B896]" />
          <span className="text-xs font-medium text-[#F5F5F7] capitalize">{place.category}</span>
        </div>
        {hasExtendedContent && !locked && (
          <div className="absolute top-3 right-3 bg-[#C9B896]/90 backdrop-blur-sm px-2 py-1 rounded-full">
            <ChevronRight className={`w-4 h-4 text-white transition-transform duration-300 ${expanded ? 'rotate-90' : ''}`} />
          </div>
        )}
        {locked && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Lock className="w-8 h-8 text-white" />
          </div>
        )}
      </div>
      <div className="p-5" onClick={handleCardClick}>
        <h3 className="font-['Manrope'] font-semibold text-[#F5F5F7] text-lg mb-2">{name}</h3>
        <p className="text-[#86868B] text-sm line-clamp-3 mb-3">{description}</p>
        
        {/* Expanded Content */}
        <motion.div
          initial={false}
          animate={{ height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          {expanded && (
            <div className="space-y-4 pt-4 border-t border-[#3A3A3C]">
              {/* Extended Description */}
              {extendedDescription && (
                <div>
                  <p className="text-[#F5F5F7] text-sm leading-relaxed">{extendedDescription}</p>
                </div>
              )}
              
              {/* Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Duration */}
                {duration && (
                  <div className="bg-[#3A3A3C] rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-[#C9B896]" />
                      <span className="text-xs font-semibold text-[#F5F5F7]">
                        {language === 'it' ? 'Durata' : 'Duration'}
                      </span>
                    </div>
                    <p className="text-xs text-[#86868B]">{duration}</p>
                  </div>
                )}
                
                {/* Best Time */}
                {bestTime && (
                  <div className="bg-[#3A3A3C] rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-4 h-4 text-[#C9B896]" />
                      <span className="text-xs font-semibold text-[#F5F5F7]">
                        {language === 'it' ? 'Periodo Migliore' : 'Best Time'}
                      </span>
                    </div>
                    <p className="text-xs text-[#86868B]">{bestTime}</p>
                  </div>
                )}
              </div>
              
              {/* Practical Info */}
              {practicalInfo && (
                <div className="bg-[#3A3A3C] rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-[#C9B896]" />
                    <span className="text-xs font-semibold text-[#F5F5F7]">
                      {language === 'it' ? 'Info Pratiche' : 'Practical Info'}
                    </span>
                  </div>
                  <p className="text-xs text-[#86868B] leading-relaxed">{practicalInfo}</p>
                </div>
              )}
              
              {/* What to Bring */}
              {whatToBring && (
                <div className="bg-[#3A3A3C] rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Backpack className="w-4 h-4 text-[#C9B896]" />
                    <span className="text-xs font-semibold text-[#F5F5F7]">
                      {language === 'it' ? 'Cosa Portare' : 'What to Bring'}
                    </span>
                  </div>
                  <p className="text-xs text-[#86868B] leading-relaxed">{whatToBring}</p>
                </div>
              )}
              
              {/* Insider Tips */}
              {insiderTips && (
                <div className="bg-gradient-to-r from-[#C9B896]/20 to-[#C9B896]/10 rounded-xl p-3 border border-[#C9B896]/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#C9B896]" />
                    <span className="text-xs font-semibold text-[#F5F5F7]">
                      {language === 'it' ? 'Consigli Insider' : 'Insider Tips'}
                    </span>
                  </div>
                  <p className="text-xs text-[#F5F5F7] leading-relaxed">{insiderTips}</p>
                </div>
              )}
            </div>
          )}
        </motion.div>
        
        {/* Original Tips (shown when not expanded) */}
        {tips && !locked && !expanded && (
          <div className="bg-[#3A3A3C] rounded-xl p-3">
            <p className="text-xs text-[#F5F5F7]">
              <span className="font-medium text-[#C9B896]">Tip: </span>
              {tips}
            </p>
          </div>
        )}
        
        {/* Maps Link */}
        {place.latitude && place.longitude && !locked && (
          <a 
            href={`https://maps.google.com/?q=${place.latitude},${place.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="mt-3 inline-flex items-center gap-1 text-[#C9B896] text-sm hover:underline"
          >
            <Map className="w-4 h-4" />
            {language === 'it' ? 'Apri in Maps' : 'Open in Maps'}
          </a>
        )}
        
        {/* Expand hint */}
        {hasExtendedContent && !locked && !expanded && (
          <p className="text-xs text-[#C9B896] mt-3 italic">
            {language === 'it' ? 'Clicca per approfondire' : 'Click to learn more'}
          </p>
        )}
      </div>
    </motion.div>
  );
};

export const ItineraryCard = ({ itinerary, language, locked = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  
  const name = language === 'it' ? itinerary.name_it : itinerary.name_en;
  const description = language === 'it' ? itinerary.description_it : itinerary.description_en;
  
  // Extended content
  const extendedDescription = language === 'it' ? itinerary.extended_description_it : itinerary.extended_description_en;
  const highlights = language === 'it' ? itinerary.highlights_it : itinerary.highlights_en;
  const bestFor = language === 'it' ? itinerary.best_for_it : itinerary.best_for_en;
  const budgetEstimate = language === 'it' ? itinerary.budget_estimate_it : itinerary.budget_estimate_en;
  const practicalTips = language === 'it' ? itinerary.practical_tips_it : itinerary.practical_tips_en;
  const packingEssentials = language === 'it' ? itinerary.packing_essentials_it : itinerary.packing_essentials_en;
  
  const hasExtendedContent = extendedDescription || highlights || bestFor;
  
  const toggleDay = (dayIdx) => {
    setExpandedDays(prev => ({
      ...prev,
      [dayIdx]: !prev[dayIdx]
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      layout
      className="bg-[#2C2C2E] rounded-2xl overflow-hidden shadow-sm border border-[#3A3A3C]"
    >
      <div 
        className="p-6 cursor-pointer hover:bg-[#3A3A3C]/50 transition-colors"
        onClick={() => !locked && setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#C9B896] rounded-2xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">{itinerary.duration_days}</span>
            </div>
            <div>
              <h3 className="font-['Manrope'] font-semibold text-[#F5F5F7] text-lg">{name}</h3>
              <p className="text-[#86868B] text-sm flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {itinerary.duration_days} {language === 'it' ? 'giorni' : 'days'}
              </p>
            </div>
          </div>
          {locked ? (
            <Lock className="w-5 h-5 text-[#86868B]" />
          ) : (
            <ChevronRight className={`w-5 h-5 text-[#86868B] transition-transform duration-300 ${expanded ? 'rotate-90' : ''}`} />
          )}
        </div>
        <p className="text-[#86868B] mt-3">{description}</p>
        
        {/* Hint to expand */}
        {hasExtendedContent && !locked && !expanded && (
          <p className="text-xs text-[#C9B896] mt-2 italic">
            {language === 'it' ? 'Clicca per approfondire' : 'Click to learn more'}
          </p>
        )}
      </div>
      
      {expanded && !locked && (
        <div className="border-t border-[#3A3A3C]">
          {/* Extended Overview Section */}
          {hasExtendedContent && (
            <div className="p-6 space-y-4 bg-[#1C1C1E]">
              {/* Extended Description */}
              {extendedDescription && (
                <div>
                  <p className="text-[#F5F5F7] text-sm leading-relaxed">{extendedDescription}</p>
                </div>
              )}
              
              {/* Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Best For */}
                {bestFor && (
                  <div className="bg-[#2C2C2E] rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className="w-4 h-4 text-[#C9B896]" />
                      <span className="text-sm font-semibold text-[#F5F5F7]">
                        {language === 'it' ? 'Ideale per' : 'Best for'}
                      </span>
                    </div>
                    <p className="text-xs text-[#86868B] leading-relaxed">{bestFor}</p>
                  </div>
                )}
                
                {/* Budget */}
                {budgetEstimate && (
                  <div className="bg-[#2C2C2E] rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <PiggyBank className="w-4 h-4 text-[#C9B896]" />
                      <span className="text-sm font-semibold text-[#F5F5F7]">
                        {language === 'it' ? 'Budget Stimato' : 'Budget Estimate'}
                      </span>
                    </div>
                    <p className="text-xs text-[#86868B] leading-relaxed">{budgetEstimate}</p>
                  </div>
                )}
              </div>
              
              {/* Highlights */}
              {highlights && (
                <div className="bg-gradient-to-r from-[#C9B896]/20 to-[#C9B896]/10 rounded-xl p-4 border border-[#C9B896]/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-[#C9B896]" />
                    <span className="text-sm font-semibold text-[#F5F5F7]">
                      {language === 'it' ? 'Momenti Imperdibili' : 'Highlights'}
                    </span>
                  </div>
                  <p className="text-xs text-[#F5F5F7] leading-relaxed">{highlights}</p>
                </div>
              )}
              
              {/* Practical Tips */}
              {practicalTips && (
                <div className="bg-[#2C2C2E] rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-[#C9B896]" />
                    <span className="text-sm font-semibold text-[#F5F5F7]">
                      {language === 'it' ? 'Consigli Pratici' : 'Practical Tips'}
                    </span>
                  </div>
                  <p className="text-xs text-[#86868B] leading-relaxed">{practicalTips}</p>
                </div>
              )}
              
              {/* Packing Essentials */}
              {packingEssentials && (
                <div className="bg-[#2C2C2E] rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Backpack className="w-4 h-4 text-[#C9B896]" />
                    <span className="text-sm font-semibold text-[#F5F5F7]">
                      {language === 'it' ? 'Da Mettere in Valigia' : 'Packing Essentials'}
                    </span>
                  </div>
                  <p className="text-xs text-[#86868B] leading-relaxed">{packingEssentials}</p>
                </div>
              )}
            </div>
          )}
          
          {/* Days Section */}
          {itinerary.days && (
            <div className="p-6 space-y-3">
              <h4 className="font-semibold text-[#F5F5F7] mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#C9B896]" />
                {language === 'it' ? 'Programma Giornaliero' : 'Daily Schedule'}
              </h4>
              
              {itinerary.days.map((day, idx) => {
                const dayTitle = language === 'it' ? day.title_it : day.title_en;
                const dayDescription = language === 'it' ? day.description_it : day.description_en;
                const dayExtended = language === 'it' ? day.extended_description_it : day.extended_description_en;
                const morningActivity = language === 'it' ? day.morning_activity_it : day.morning_activity_en;
                const afternoonActivity = language === 'it' ? day.afternoon_activity_it : day.afternoon_activity_en;
                const eveningSuggestion = language === 'it' ? day.evening_suggestion_it : day.evening_suggestion_en;
                const insiderTip = language === 'it' ? day.insider_tip_it : day.insider_tip_en;
                const photoSpots = language === 'it' ? day.photo_spots_it : day.photo_spots_en;
                const hasDayExtended = dayExtended || morningActivity || afternoonActivity;
                const isDayExpanded = expandedDays[idx];
                
                return (
                  <div 
                    key={idx} 
                    className={`bg-[#3A3A3C] rounded-xl overflow-hidden transition-all duration-300 ${hasDayExtended ? 'cursor-pointer hover:bg-[#3A3A3C]/80' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (hasDayExtended) toggleDay(idx);
                    }}
                  >
                    <div className="p-4 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-[#C9B896] rounded-xl flex items-center justify-center text-sm font-bold text-white">
                          {day.day}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h5 className="font-medium text-[#F5F5F7]">{dayTitle}</h5>
                          {hasDayExtended && (
                            <ChevronRight className={`w-4 h-4 text-[#86868B] transition-transform duration-300 ${isDayExpanded ? 'rotate-90' : ''}`} />
                          )}
                        </div>
                        <p className="text-[#86868B] text-sm mt-1">{dayDescription}</p>
                        
                        {/* Expanded Day Content */}
                        {isDayExpanded && hasDayExtended && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-4 pt-4 border-t border-[#2C2C2E] space-y-3"
                          >
                            {dayExtended && (
                              <p className="text-sm text-[#F5F5F7] leading-relaxed">{dayExtended}</p>
                            )}
                            
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              {morningActivity && (
                                <div className="bg-[#2C2C2E] rounded-lg p-3">
                                  <p className="text-xs font-semibold text-[#C9B896] mb-1">Mattina</p>
                                  <p className="text-xs text-[#86868B]">{morningActivity}</p>
                                </div>
                              )}
                              {afternoonActivity && (
                                <div className="bg-[#2C2C2E] rounded-lg p-3">
                                  <p className="text-xs font-semibold text-[#C9B896] mb-1">Pomeriggio</p>
                                  <p className="text-xs text-[#86868B]">{afternoonActivity}</p>
                                </div>
                              )}
                              {eveningSuggestion && (
                                <div className="bg-[#2C2C2E] rounded-lg p-3">
                                  <p className="text-xs font-semibold text-[#C9B896] mb-1">Sera</p>
                                  <p className="text-xs text-[#86868B]">{eveningSuggestion}</p>
                                </div>
                              )}
                            </div>
                            
                            {(insiderTip || photoSpots) && (
                              <div className="flex flex-col sm:flex-row gap-2">
                                {insiderTip && (
                                  <div className="flex-1 bg-[#C9B896]/10 rounded-lg p-3 border border-[#C9B896]/20">
                                    <p className="text-xs font-semibold text-[#C9B896] mb-1">Tip Insider</p>
                                    <p className="text-xs text-[#F5F5F7]">{insiderTip}</p>
                                  </div>
                                )}
                                {photoSpots && (
                                  <div className="flex-1 bg-[#2C2C2E] rounded-lg p-3">
                                    <p className="text-xs font-semibold text-[#C9B896] mb-1 flex items-center gap-1">
                                      <Camera className="w-3 h-3" /> Spot Foto
                                    </p>
                                    <p className="text-xs text-[#86868B]">{photoSpots}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </motion.div>
                        )}
                        
                        {/* Hint to expand day */}
                        {hasDayExtended && !isDayExpanded && (
                          <p className="text-xs text-[#C9B896] mt-2 italic">
                            {language === 'it' ? 'Clicca per approfondire' : 'Click to learn more'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export const TipCard = ({ tip, language, locked = false }) => {
  const [expanded, setExpanded] = useState(false);
  
  const title = language === 'it' ? tip.title_it : tip.title_en;
  const content = language === 'it' ? tip.content_it : tip.content_en;
  const Icon = iconMap[tip.icon] || Lightbulb;
  
  // Extended content
  const extendedContent = language === 'it' ? tip.extended_content_it : tip.extended_content_en;
  const keyPoints = language === 'it' ? tip.key_points_it : tip.key_points_en;
  const commonMistakes = language === 'it' ? tip.common_mistakes_it : tip.common_mistakes_en;
  const proTips = language === 'it' ? tip.pro_tips_it : tip.pro_tips_en;
  const usefulLinks = language === 'it' ? tip.useful_links_it : tip.useful_links_en;
  
  const hasExtendedContent = extendedContent || keyPoints || proTips;

  const handleClick = () => {
    if (!locked && hasExtendedContent) {
      setExpanded(!expanded);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      layout
      className={`bg-[#2C2C2E] rounded-2xl overflow-hidden shadow-sm border border-[#3A3A3C] ${locked ? 'opacity-60' : ''} ${hasExtendedContent && !locked ? 'cursor-pointer' : ''}`}
    >
      {tip.image_url && (
        <div className="relative h-40 overflow-hidden" onClick={handleClick}>
          <img src={tip.image_url} alt={title} className="w-full h-full object-cover" />
          {hasExtendedContent && !locked && (
            <div className="absolute top-3 right-3 bg-[#C9B896]/90 backdrop-blur-sm px-2 py-1 rounded-full">
              <ChevronRight className={`w-4 h-4 text-white transition-transform duration-300 ${expanded ? 'rotate-90' : ''}`} />
            </div>
          )}
          {locked && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
          )}
        </div>
      )}
      <div className="p-5" onClick={handleClick}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-[#C9B896]/20 rounded-xl flex items-center justify-center">
            <Icon className="w-5 h-5 text-[#C9B896]" />
          </div>
          <h3 className="font-['Manrope'] font-semibold text-[#F5F5F7]">{title}</h3>
        </div>
        {!locked && <p className="text-[#86868B] text-sm leading-relaxed">{content}</p>}
        
        {/* Expanded Content */}
        {expanded && !locked && hasExtendedContent && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t border-[#3A3A3C] space-y-4"
          >
            {/* Extended Description */}
            {extendedContent && (
              <div>
                <p className="text-[#F5F5F7] text-sm leading-relaxed">{extendedContent}</p>
              </div>
            )}
            
            {/* Key Points */}
            {keyPoints && (
              <div className="bg-[#3A3A3C] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-4 h-4 text-[#C9B896]" />
                  <span className="text-sm font-semibold text-[#F5F5F7]">
                    {language === 'it' ? 'Punti Chiave' : 'Key Points'}
                  </span>
                </div>
                <ul className="space-y-2">
                  {keyPoints.split(';').map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs text-[#86868B]">
                      <span className="text-[#C9B896] mt-0.5">•</span>
                      <span>{point.trim()}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Common Mistakes & Pro Tips Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Common Mistakes */}
              {commonMistakes && (
                <div className="bg-[#3A3A3C] rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-4 h-4 text-orange-400" />
                    <span className="text-sm font-semibold text-[#F5F5F7]">
                      {language === 'it' ? 'Errori da Evitare' : 'Common Mistakes'}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {commonMistakes.split('.').filter(m => m.trim()).slice(0, 3).map((mistake, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-[#86868B]">
                        <span className="text-orange-400 mt-0.5">✗</span>
                        <span>{mistake.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Pro Tips */}
              {proTips && (
                <div className="bg-gradient-to-r from-[#C9B896]/20 to-[#C9B896]/10 rounded-xl p-4 border border-[#C9B896]/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="w-4 h-4 text-[#C9B896]" />
                    <span className="text-sm font-semibold text-[#F5F5F7]">
                      {language === 'it' ? 'Consigli Pro' : 'Pro Tips'}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {proTips.split('.').filter(t => t.trim()).slice(0, 3).map((proTip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-[#F5F5F7]">
                        <span className="text-[#C9B896] mt-0.5">★</span>
                        <span>{proTip.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            {/* Useful Resources */}
            {usefulLinks && (
              <div className="bg-[#3A3A3C] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Smartphone className="w-4 h-4 text-[#C9B896]" />
                  <span className="text-sm font-semibold text-[#F5F5F7]">
                    {language === 'it' ? 'Risorse Utili' : 'Useful Resources'}
                  </span>
                </div>
                <p className="text-xs text-[#86868B] leading-relaxed">{usefulLinks}</p>
              </div>
            )}
          </motion.div>
        )}
        
        {/* Hint to expand */}
        {hasExtendedContent && !locked && !expanded && (
          <p className="text-xs text-[#C9B896] mt-3 italic">
            {language === 'it' ? 'Clicca per approfondire' : 'Click to learn more'}
          </p>
        )}
      </div>
    </motion.div>
  );
};

const GuidePremium = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  
  const [guide, setGuide] = useState(null);
  const [content, setContent] = useState(null);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [activeTab, setActiveTab] = useState('places');
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [gumroadUrl, setGumroadUrl] = useState('');
  
  const previewMode = searchParams.get('preview');
  const accessToken = searchParams.get('access');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get guide info
        const guideRes = await axios.get(`${API}/guides/${id}`);
        setGuide(guideRes.data);
        
        // Get Gumroad link
        try {
          const gumroadRes = await axios.get(`${API}/gumroad/link/${id}`);
          setGumroadUrl(gumroadRes.data.gumroad_url || '');
        } catch (e) {
          console.log('Gumroad link not available');
        }
        
        // Get author
        const authorRes = await axios.get(`${API}/author`);
        setAuthor(authorRes.data);
        
        // Send readable page view to Google Analytics
        if (window.gtag) {
          const guideNames = {
            'iceland': 'Guida Islanda',
            'japan': 'Guida Giappone',
            'new-zealand': 'Guida Nuova Zelanda',
            'sicily': 'Guida Sicilia'
          };
          const guideName = guideNames[id] || id;
          const pageType = accessToken ? 'completa' : 'anteprima';
          
          window.gtag('event', 'page_view', {
            page_title: `${guideName} (${pageType})`,
            page_location: `${window.location.origin}/guide/${id}`,
            page_path: `/guide/${id}`
          });
          
          // Custom event for detailed tracking
          window.gtag('event', 'guide_view', {
            guide_id: id,
            guide_name: guideName,
            access_type: accessToken ? 'full_access' : 'preview'
          });
        }
        
        // Check if preview=full mode (admin preview)
        if (previewMode === 'full') {
          const fullContent = await axios.get(`${API}/guide-content-full/${id}?preview=admin`);
          setContent(fullContent.data.content);
          setHasAccess(true);
          setIsPreviewMode(true);
        }
        // Check if has access via token in URL
        else if (accessToken) {
          try {
            // Verify token and get full content
            const verifyRes = await axios.get(`${API}/guide-access/verify/${accessToken}`);
            if (verifyRes.data.valid && verifyRes.data.guide?.id === id) {
              setContent(verifyRes.data.content);
              setHasAccess(true);
            } else {
              throw new Error('Invalid token');
            }
          } catch {
            // Token invalid, get preview
            const preview = await axios.get(`${API}/guide-content/${id}`);
            setContent(preview.data);
            setHasAccess(false);
          }
        } else {
          // No token, get preview
          const preview = await axios.get(`${API}/guide-content/${id}`);
          setContent(preview.data);
          setHasAccess(false);
        }
      } catch (error) {
        console.error('Error fetching guide:', error);
        navigate('/guides');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, accessToken, previewMode, navigate]);

  if (loading) {
    return (
      <main className="pt-20 min-h-screen bg-[#1C1C1E]">
        <div className="max-w-[1400px] mx-auto px-6 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-[400px] bg-[#2C2C2E] rounded-3xl" />
            <div className="h-8 bg-[#2C2C2E] rounded w-1/3" />
            <div className="grid grid-cols-3 gap-6">
              {[1,2,3].map(i => <div key={i} className="h-64 bg-[#2C2C2E] rounded-2xl" />)}
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!guide) return null;

  const title = language === 'it' ? guide.title_it : guide.title_en;
  const description = language === 'it' ? guide.description_it : guide.description_en;
  const bio = language === 'it' ? author?.bio_it : author?.bio_en;
  const tagline = language === 'it' ? author?.tagline_it : author?.tagline_en;

  const places = hasAccess ? (content?.places || []) : (content?.preview_places || []);
  const itineraries = hasAccess ? (content?.itineraries || []) : (content?.preview_itineraries || []);
  const tips = hasAccess ? (content?.tips || []) : (content?.preview_tips || []);

  const tabs = [
    { id: 'map', label: language === 'it' ? 'Mappa' : 'Map', count: null, icon: '🗺️' },
    { id: 'places', label: language === 'it' ? 'Luoghi' : 'Places', count: hasAccess ? places.length : content?.places_count },
    { id: 'itineraries', label: language === 'it' ? 'Itinerari' : 'Itineraries', count: hasAccess ? itineraries.length : content?.itineraries_count },
    { id: 'tips', label: 'Tips', count: hasAccess ? tips.length : content?.tips_count },
    { id: 'checklist', label: 'Checklist', count: null, icon: '✓' }
  ];

  return (
    <main className="pt-20 min-h-screen bg-[#1C1C1E]" data-testid="guide-premium-page">
      {/* Admin Preview Banner */}
      {isPreviewMode && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 px-6 text-center font-medium">
          <span className="mr-2">👁️</span>
          {language === 'it' 
            ? 'MODALITÀ ANTEPRIMA ADMIN - Questa è la guida completa che i clienti vedranno dopo l\'acquisto'
            : 'ADMIN PREVIEW MODE - This is the full guide customers will see after purchase'
          }
        </div>
      )}
      
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[500px]">
        <img 
          src={guide.image_url} 
          alt={title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
          <div className="max-w-[1400px] mx-auto">
            <Link 
              to="/guides"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {language === 'it' ? 'Tutte le Guide' : 'All Guides'}
            </Link>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-['Manrope'] font-bold text-5xl md:text-7xl mb-4"
            >
              <span className="text-white">{language === 'it' ? 'La Guida Definitiva' : 'The Ultimate Guide'}</span>
              <br />
              <span className="text-[#C9B896]">{title}</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-white/80 text-lg md:text-xl max-w-2xl"
            >
              {description}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#2C2C2E] border-b border-[#3A3A3C]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-16 py-6">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-8">
              <div className="text-center">
                <p className="font-['Manrope'] font-bold text-2xl text-[#F5F5F7]">
                  {content?.places_count || places.length}
                </p>
                <p className="text-[#86868B] text-sm">{language === 'it' ? 'Luoghi' : 'Places'}</p>
              </div>
              <div className="text-center">
                <p className="font-['Manrope'] font-bold text-2xl text-[#F5F5F7]">
                  {content?.itineraries_count || itineraries.length}
                </p>
                <p className="text-[#86868B] text-sm">{language === 'it' ? 'Itinerari' : 'Itineraries'}</p>
              </div>
              <div className="text-center">
                <p className="font-['Manrope'] font-bold text-2xl text-[#F5F5F7]">
                  {content?.tips_count || tips.length}
                </p>
                <p className="text-[#86868B] text-sm">Tips</p>
              </div>
            </div>
            
            {!hasAccess && (
              <a
                href={gumroadUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#C9B896] text-white px-8 py-3 rounded-full font-medium hover:bg-[#A89770] transition-colors flex items-center gap-2"
                onClick={(e) => {
                  if (!gumroadUrl) {
                    e.preventDefault();
                    alert(language === 'it' ? 'Link di acquisto non disponibile' : 'Purchase link not available');
                  }
                }}
              >
                {language === 'it' ? 'Sblocca Guida' : 'Unlock Guide'} - €{guide.price}
              </a>
            )}
            
            {hasAccess && (
              <div className="flex items-center gap-4">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/guide-pdf/${accessToken || 'preview'}?language=${language}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#3A3A3C] hover:bg-[#4A4A4C] text-white px-6 py-3 rounded-full font-medium transition-colors flex items-center gap-2"
                  data-testid="download-pdf-btn"
                >
                  <FileDown className="w-5 h-5" />
                  {language === 'it' ? 'Scarica PDF' : 'Download PDF'}
                </a>
                <div className="flex items-center gap-2 text-green-500">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">{language === 'it' ? 'Accesso Completo' : 'Full Access'}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Author Section */}
      <section className="bg-[#2C2C2E]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-16 py-12">
          <div className="flex flex-col md:flex-row items-center gap-8">
            {id === 'iceland' ? (
              <img 
                src="/author_iceland.jpg" 
                alt={author?.name || 'Danilo'}
                className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-2 border-[#C9B896]"
              />
            ) : id === 'sicily' ? (
              <img 
                src="/author_sicily.jpg" 
                alt={author?.name || 'Danilo'}
                className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-2 border-[#C9B896]"
              />
            ) : id === 'new-zealand' ? (
              <img 
                src="/author_nz.jpg" 
                alt={author?.name || 'Danilo'}
                className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-2 border-[#C9B896]"
              />
            ) : id === 'japan' ? (
              <img 
                src="/author_japan.jpg" 
                alt={author?.name || 'Danilo'}
                className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-2 border-[#C9B896]"
              />
            ) : (
              <div className="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-[#C9B896] to-[#00C7BE] rounded-full flex items-center justify-center text-white text-4xl font-bold">
                {author?.name?.charAt(0) || 'D'}
              </div>
            )}
            <div className="text-center md:text-left">
              <p className="text-[#C9B896] text-sm font-medium mb-1">
                {language === 'it' ? 'Guida creata da' : 'Guide by'}
              </p>
              <h2 className="font-['Manrope'] font-bold text-[#F5F5F7] text-2xl md:text-3xl mb-2">
                {author?.name || 'Danilo'}
              </h2>
              <p className="text-[#86868B] max-w-2xl">{bio}</p>
              {tagline && (
                <p className="text-[#C9B896] font-medium mt-3 flex items-center justify-center md:justify-start gap-2">
                  <Star className="w-4 h-4" />
                  {tagline}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Content Tabs */}
      <section className="sticky top-16 md:top-20 bg-[#2C2C2E] border-b border-[#3A3A3C] z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-16">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 md:px-6 py-4 font-medium transition-colors relative whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id 
                    ? 'text-[#C9B896]' 
                    : 'text-[#86868B] hover:text-[#F5F5F7]'
                }`}
              >
                {tab.label}
                {tab.count !== null && (
                  <span className="ml-2 text-sm bg-[#3A3A3C] px-2 py-0.5 rounded-full">
                    {tab.count}
                  </span>
                )}
                {activeTab === tab.id && (
                  <motion.div 
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#C9B896]" 
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6 md:px-16">
          {/* Interactive Map Tab */}
          {activeTab === 'map' && hasAccess && (
            <div>
              <h2 className="font-['Manrope'] font-semibold text-[#F5F5F7] text-2xl mb-6">
                {language === 'it' ? 'Mappa Interattiva' : 'Interactive Map'}
              </h2>
              <p className="text-[#86868B] mb-6">
                {language === 'it' 
                  ? 'Esplora tutti i luoghi della guida sulla mappa. Clicca sui pin per vedere i dettagli.'
                  : 'Explore all guide locations on the map. Click on pins to see details.'}
              </p>
              <div className="relative">
                <InteractiveMap places={places} language={language} />
              </div>
            </div>
          )}

          {activeTab === 'map' && !hasAccess && (
            <div className="bg-[#2C2C2E] rounded-2xl p-12 text-center border border-[#3A3A3C]">
              <Lock className="w-12 h-12 text-[#86868B] mx-auto mb-4" />
              <h3 className="font-['Manrope'] font-semibold text-[#F5F5F7] text-xl mb-2">
                {language === 'it' ? 'Mappa Interattiva' : 'Interactive Map'}
              </h3>
              <p className="text-[#86868B] mb-6">
                {language === 'it' 
                  ? 'Sblocca la guida per accedere alla mappa interattiva con tutti i luoghi'
                  : 'Unlock the guide to access the interactive map with all locations'}
              </p>
              <a
                href={gumroadUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#C9B896] text-white px-8 py-3 rounded-full font-medium hover:bg-[#A89770] transition-colors"
                onClick={(e) => {
                  if (!gumroadUrl) {
                    e.preventDefault();
                    alert(language === 'it' ? 'Link di acquisto non disponibile' : 'Purchase link not available');
                  }
                }}
              >
                {language === 'it' ? 'Sblocca Ora' : 'Unlock Now'} - €{guide.price}
              </a>
            </div>
          )}

          {/* Places */}
          {activeTab === 'places' && (
            <div>
              <h2 className="font-['Manrope'] font-semibold text-[#F5F5F7] text-2xl mb-6">
                {language === 'it' ? 'Luoghi da Visitare' : 'Places to Visit'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {places.map((place, idx) => (
                  <PlaceCard key={place.id || idx} place={place} language={language} />
                ))}
                {!hasAccess && Array((content?.places_count || 0) - places.length).fill(0).map((_, idx) => (
                  <PlaceCard 
                    key={`locked-${idx}`} 
                    place={{
                      name_it: language === 'it' ? 'Contenuto Bloccato' : 'Locked Content',
                      name_en: 'Locked Content',
                      description_it: language === 'it' ? 'Acquista la guida per sbloccare' : 'Purchase the guide to unlock',
                      description_en: 'Purchase the guide to unlock',
                      image_url: 'https://images.unsplash.com/photo-1557683316-973673baf926?w=400',
                      category: 'sightseeing'
                    }} 
                    language={language} 
                    locked={true} 
                  />
                ))}
              </div>
            </div>
          )}

          {/* Itineraries */}
          {activeTab === 'itineraries' && (
            <div>
              <h2 className="font-['Manrope'] font-semibold text-[#F5F5F7] text-2xl mb-6">
                {language === 'it' ? 'Itinerari Consigliati' : 'Recommended Itineraries'}
              </h2>
              {hasAccess ? (
                <div className="space-y-4">
                  {itineraries.map((itin, idx) => (
                    <ItineraryCard key={itin.id || idx} itinerary={itin} language={language} />
                  ))}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Sample Itinerary */}
                  {itineraries.filter(it => it.is_sample).map((itin, idx) => (
                    <div key={itin.id || idx}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-3 py-1 bg-[#C9B896]/20 text-[#C9B896] text-xs font-medium rounded-full">
                          {language === 'it' ? 'ANTEPRIMA GRATUITA' : 'FREE PREVIEW'}
                        </span>
                      </div>
                      <ItineraryCard itinerary={itin} language={language} />
                    </div>
                  ))}
                  
                  {/* Locked message */}
                  <div className="bg-[#2C2C2E] rounded-2xl p-8 text-center border border-[#3A3A3C]">
                    <Lock className="w-10 h-10 text-[#86868B] mx-auto mb-3" />
                    <h3 className="font-['Manrope'] font-semibold text-[#F5F5F7] text-lg mb-2">
                      +{(content?.itineraries_count || itineraries.length) - 1} {language === 'it' ? 'altri itinerari' : 'more itineraries'}
                    </h3>
                    <p className="text-[#86868B] text-sm mb-4">
                      {language === 'it' 
                        ? 'Sblocca la guida per accedere a tutti gli itinerari dettagliati'
                        : 'Unlock the guide to access all detailed itineraries'}
                    </p>
                    <a
                      href={gumroadUrl || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#C9B896] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#A89770] transition-colors text-sm"
                      onClick={(e) => {
                        if (!gumroadUrl) {
                          e.preventDefault();
                          alert(language === 'it' ? 'Link di acquisto non disponibile' : 'Purchase link not available');
                        }
                      }}
                    >
                      {language === 'it' ? 'Sblocca Tutti' : 'Unlock All'} - €{guide.price}
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tips */}
          {activeTab === 'tips' && (
            <div>
              <h2 className="font-['Manrope'] font-semibold text-[#F5F5F7] text-2xl mb-6">
                {language === 'it' ? 'Consigli di Viaggio' : 'Travel Tips'}
              </h2>
              {hasAccess ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {tips.map((tip, idx) => (
                    <TipCard key={tip.id || idx} tip={tip} language={language} />
                  ))}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Sample Tips */}
                  {tips.filter(t => t.is_sample).length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <span className="px-3 py-1 bg-[#C9B896]/20 text-[#C9B896] text-xs font-medium rounded-full">
                          {language === 'it' ? 'ANTEPRIMA GRATUITA' : 'FREE PREVIEW'}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {tips.filter(t => t.is_sample).map((tip, idx) => (
                          <TipCard key={tip.id || idx} tip={tip} language={language} />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Locked Tips */}
                  <div className="bg-[#2C2C2E] rounded-2xl p-8 text-center border border-[#3A3A3C]">
                    <Lock className="w-10 h-10 text-[#86868B] mx-auto mb-3" />
                    <h3 className="font-['Manrope'] font-semibold text-[#F5F5F7] text-lg mb-2">
                      +{(content?.tips_count || 0) - tips.filter(t => t.is_sample).length} {language === 'it' ? 'altri consigli' : 'more tips'}
                    </h3>
                    <p className="text-[#86868B] text-sm mb-4">
                      {language === 'it' 
                        ? 'Sblocca la guida per accedere a tutti i consigli insider'
                        : 'Unlock the guide to access all insider tips'}
                    </p>
                    <a
                      href={gumroadUrl || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#C9B896] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#A89770] transition-colors text-sm"
                      onClick={(e) => {
                        if (!gumroadUrl) {
                          e.preventDefault();
                          alert(language === 'it' ? 'Link di acquisto non disponibile' : 'Purchase link not available');
                        }
                      }}
                    >
                      {language === 'it' ? 'Sblocca Tutti' : 'Unlock All'} - €{guide.price}
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Checklist */}
          {activeTab === 'checklist' && (
            <div className="animate-fade-in">
              <GuideChecklist 
                guideId={id} 
                guideTitle={guide?.title_en || guide?.title_it} 
              />
            </div>
          )}
        </div>
      </section>

      {/* CTA Footer */}
      {!hasAccess && (
        <section className="bg-[#0C0C0E] py-16">
          <div className="max-w-[1400px] mx-auto px-6 md:px-16 text-center">
            <h2 className="font-['Manrope'] font-bold text-3xl md:text-4xl mb-4 text-white">
              {language === 'it' ? 'Pronto a Esplorare?' : 'Ready to Explore?'}
            </h2>
            <p className="text-[#86868B] text-lg mb-8 max-w-2xl mx-auto">
              {language === 'it' 
                ? `Sblocca tutti i ${content?.places_count} luoghi, ${content?.itineraries_count} itinerari e ${content?.tips_count} consigli esclusivi`
                : `Unlock all ${content?.places_count} places, ${content?.itineraries_count} itineraries and ${content?.tips_count} exclusive tips`}
            </p>
            <a
              href={gumroadUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#C9B896] text-white px-10 py-4 rounded-full text-lg font-medium hover:bg-[#A89770] transition-all hover:scale-105"
              onClick={(e) => {
                if (!gumroadUrl) {
                  e.preventDefault();
                  alert(language === 'it' ? 'Link di acquisto non disponibile' : 'Purchase link not available');
                }
              }}
            >
              {language === 'it' ? 'Acquista la Guida' : 'Get the Guide'} - €{guide.price}
            </a>
          </div>
        </section>
      )}
    </main>
  );
};

export default GuidePremium;
